import { useCallback, useEffect, useMemo, useState } from "react";
import { Duration } from "luxon";
import { CommonMentionKindForRoutableKind, MemRoutableItemKind } from "@mem-labs/common-editor";
import { useAppStore } from "@/store";
import { SidePanelTab } from "@/store/routing/types";
import type { SmartCardRow } from "@/domains/database";
import { IntervalObservable } from "@/store/common";
import { formatRelativeUpdateTime } from "../helpers";
import { useContextId } from "./use-context-id";
import { useDataSource } from "./use-data-source";
import { useSnapshots } from "./use-snapshots";
import { useRelatedEntities } from "./use-related-entities";
import {
  HeadsUpActivityState,
  type HeadsUpActivitySlice,
  type HeadsUpContentSlice,
  type HeadsUpInteractionSlice,
  type HeadsUpResourceSlice,
  type HeadsUpSidebarModel,
  type HeadsUpUiSlice,
} from "../types";
import { HeadsUpViewState, type HeadsUpClickHandlers } from "@/components/heads-up/context";

const ONE_MINUTE = Duration.fromObject({ minutes: 1 });

function partitionCards(cards: SmartCardRow[]) {
  const primary: SmartCardRow[] = [];
  const fromFindMore: SmartCardRow[] = [];

  for (const card of cards) {
    if ((card as { originated_from_find_more?: boolean | null }).originated_from_find_more) {
      fromFindMore.push(card);
    } else {
      primary.push(card);
    }
  }

  return { primary, fromFindMore };
}

function deriveActivityState(
  statuses: ReturnType<typeof useDataSource>["statuses"]
): HeadsUpActivityState {
  if (statuses.isHydrating) return HeadsUpActivityState.Hydrating;
  if (statuses.isRegenerating) return HeadsUpActivityState.Regenerating;
  if (statuses.isRefreshing) return HeadsUpActivityState.Refreshing;
  if (statuses.isGenerating) return HeadsUpActivityState.Generating;
  if (statuses.isFindingMore) return HeadsUpActivityState.FindingMore;
  return HeadsUpActivityState.Idle;
}

function deriveViewState({
  activeNoteId,
  errorForContext,
  statuses,
  view,
  isViewLoading,
  isCardsLoading,
  displayedCards,
}: {
  activeNoteId: string | null;
  errorForContext: string | null;
  statuses: ReturnType<typeof useDataSource>["statuses"];
  view: ReturnType<typeof useDataSource>["view"];
  isViewLoading: boolean;
  isCardsLoading: boolean;
  displayedCards: SmartCardRow[];
}): HeadsUpViewState {
  if (!activeNoteId) {
    return HeadsUpViewState.FullEmpty;
  }

  if (errorForContext) {
    return HeadsUpViewState.Error;
  }

  if (statuses.isHydrating) {
    return HeadsUpViewState.Hydrating;
  }

  if (statuses.isGenerating || statuses.isRegenerating) {
    return HeadsUpViewState.Generating;
  }

  if (!view) {
    if (isViewLoading || isCardsLoading) {
      return HeadsUpViewState.Generating;
    }
    return HeadsUpViewState.GeneratedNoContent;
  }

  if (displayedCards.length > 0) {
    return HeadsUpViewState.GeneratedHasContent;
  }

  if (isCardsLoading) {
    return HeadsUpViewState.Generating;
  }

  return HeadsUpViewState.GeneratedNoContent;
}

export function useHeadsUpSidebar(): HeadsUpSidebarModel {
  const { store } = useAppStore();
  const [isSidebarHovered, setIsSidebarHovered] = useState(false);

  const contextInfo = useContextId(store);
  const dataSource = useDataSource(store, contextInfo);
  const {
    refreshSmartView,
    regenerateSmartView,
    findMore: findMoreAction,
    ensureNoteHydrated,
    isNoteLoading,
  } = dataSource.actions;

  const snapshotState = useSnapshots({
    smartViewId: dataSource.smartViewId,
    viewRefreshedAt: dataSource.view?.refreshed_at ?? null,
    cards: dataSource.cards,
    cardsSignature: dataSource.cardsSignature,
    isSidebarHovered,
    isCardsLoading: dataSource.isCardsLoading,
    isViewLoading: dataSource.isViewLoading,
    statuses: dataSource.statuses,
    refreshSmartView,
  });

  const timeInterval = useMemo(() => new IntervalObservable({ duration: ONE_MINUTE }), []);

  useEffect(() => () => timeInterval.dispose(), [timeInterval]);

  const formattedRelativeTime = useMemo(() => {
    // make reactive to ticking interval
    timeInterval.value;
    return formatRelativeUpdateTime(snapshotState.renderedRefreshedAt);
  }, [snapshotState.renderedRefreshedAt, timeInterval.value]);

  useRelatedEntities({
    store,
    activeNoteId: contextInfo.activeNoteId,
    displayedCards: snapshotState.displayedCards,
    ensureNoteHydrated,
  });

  const cardRenderOptions = useMemo(() => {
    return {
      isHoverable: store.publicAppStore.interface.isHoverable,
      getNotePreviewInfo: (noteId: string) => {
        const note = store.notes.getNoteObservableById({ noteId });
        if (!note) return undefined;
        return {
          isNoteContentDocumentLoaded: note.isNoteContentDocumentLoaded,
          fetchNoteContentDocument: () => note.fetchNoteContentDocument(),
          noteObservable: note,
        };
      },
      getCollectionInfo: (collectionId: string) => {
        const collection = store.collections.getCollectionObservableById({ collectionId });
        if (!collection) return undefined;
        return {
          id: collection.id,
          sizeData: collection.itemList.sizeData,
          description: collection.description ?? null,
        };
      },
    };
  }, [store.publicAppStore.interface.isHoverable, store.notes, store.collections]);

  const { primary, fromFindMore } = useMemo(
    () => partitionCards(snapshotState.displayedCards),
    [snapshotState.displayedCards]
  );

  const viewState = deriveViewState({
    activeNoteId: contextInfo.activeNoteId,
    errorForContext: dataSource.errorForContext,
    statuses: dataSource.statuses,
    view: dataSource.view,
    isViewLoading: dataSource.isViewLoading,
    isCardsLoading: dataSource.isCardsLoading,
    displayedCards: snapshotState.displayedCards,
  });

  const hasView = dataSource.view !== null;
  const isBusy =
    dataSource.statuses.isHydrating ||
    dataSource.statuses.isGenerating ||
    dataSource.statuses.isRefreshing ||
    dataSource.statuses.isRegenerating ||
    dataSource.statuses.isFindingMore;

  const canRefresh = Boolean(contextInfo.activeNoteId && hasView && !isBusy);
  const canRegenerate = Boolean(hasView && !isBusy);
  const canFindMore = Boolean(dataSource.smartViewId) && !dataSource.hasFoundContentBefore;

  const activity: HeadsUpActivitySlice = {
    state: deriveActivityState(dataSource.statuses),
    isBusy,
    isGenerating: dataSource.statuses.isGenerating,
    isRefreshing: dataSource.statuses.isRefreshing,
    isRegenerating: dataSource.statuses.isRegenerating,
    isFindingMore: dataSource.statuses.isFindingMore,
    hasFoundMore: dataSource.hasFoundContentBefore,
    canRefresh,
    canRegenerate,
    canFindMore,
  };

  const pendingSnapshot = snapshotState.hasPendingSnapshot
    ? { accept: snapshotState.acceptPendingSnapshot }
    : null;

  const content: HeadsUpContentSlice = {
    cards: snapshotState.displayedCards,
    primaryCards: primary,
    findMoreCards: fromFindMore,
    hasView,
    viewState,
    refreshedAt: snapshotState.renderedRefreshedAt,
    formattedRelativeTime,
    pendingSnapshot,
    error: dataSource.errorForContext,
    isLoading: dataSource.isCardsLoading,
    visibility: {
      get: () => undefined,
      set: () => {},
    },
  };

  const clickHandlers: HeadsUpClickHandlers = useMemo(() => {
    const handleClickMemItem = (itemKind: MemRoutableItemKind, itemId: string) => {
      const mentionKind = CommonMentionKindForRoutableKind(itemKind);
      if (!mentionKind) return;

      store.navigation.goToMention({
        mentionKind,
        id: itemId,
        url: `/${itemKind}/${itemId}`,
        highlightText: undefined,
        // Force split pane by simulating meta key click
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        keys: { metaKey: true } as any,
        openInBackground: false,
      });
    };

    return {
      noteClick: noteId => event => {
        event.preventDefault();
        handleClickMemItem(MemRoutableItemKind.NOTES, noteId);
      },
      collectionClick: collectionId => event => {
        event.preventDefault();
        handleClickMemItem(MemRoutableItemKind.COLLECTIONS, collectionId);
      },
    };
  }, [store.navigation]);

  const refresh = useCallback(() => {
    refreshSmartView();
  }, [refreshSmartView]);

  const regenerate = useCallback(() => {
    regenerateSmartView();
  }, [regenerateSmartView]);

  const findMore = useCallback(() => {
    findMoreAction();
  }, [findMoreAction]);

  const openHeadsUp = useCallback(() => {
    store.sidePanel.goToHeadsUp();
  }, [store.sidePanel]);

  const interactions: HeadsUpInteractionSlice = {
    refresh,
    regenerate,
    findMore,
    openHeadsUp,
    clickHandlers,
  };

  const resources: HeadsUpResourceSlice = {
    loadNote: ensureNoteHydrated,
    isNoteLoading,
    cardRenderOptions,
  };

  const ui: HeadsUpUiSlice = {
    isHovered: isSidebarHovered,
    setHovered: setIsSidebarHovered,
    isChatOverlayOpen: store.sidePanel.activeTabId === SidePanelTab.MEM_IT,
  };

  return {
    context: {
      contextId: contextInfo.contextId,
      activeNoteId: contextInfo.activeNoteId,
      activeNoteTitle: dataSource.noteTitle,
      isDatabaseReady: contextInfo.isDatabaseReady,
    },
    content,
    activity,
    interactions,
    resources,
    ui,
  };
}
